// hooks/useOrderConfirmation.ts
import { useState } from "react";

interface OrderConfirmationOptions {
  orderData: any;
  id: string | null;
  time: { year: string; month: string; day: string; hours: string; minutes: string };
  confirmOrder: (payload: any) => Promise<any>;
  updateCustomer: (payload: any) => Promise<any>;
}

export const useOrderConfirmation = ({ orderData, id, time, confirmOrder, updateCustomer }: OrderConfirmationOptions) => {
  const [receivedAmount, setReceivedAmount] = useState<number>(0);

  const calculateRemainingAmount = (): number => {
    const totalPrice = Number(orderData?.totalOrderPrice) || 0;
    return totalPrice - receivedAmount;
  };

  // Group services by car for better organization
  const groupServicesByCar = () => {
    if (!orderData?.serviceDetails) return [];

    const carsMap = new Map();

    orderData.serviceDetails.forEach((service: { carNumber: string }) => {
      const carNumber = service.carNumber || "Default Car";
      if (!carsMap.has(carNumber)) {
        carsMap.set(carNumber, []);
      }
      carsMap.get(carNumber).push(service);
    });

    return Array.from(carsMap.entries()).map(([carNumber, services]) => ({
      carNumber,
      services,
    }));
  };

  // Convert old productDetails to new Products format
  const formatProducts = (serviceDetails: any[]) => {
    const products: any[] = [];

    serviceDetails.forEach((service) => {
      service.productDetails.forEach((p: any) => {
        products.push({
          id: p._id || p.id || "", // ensure backend ID
          name: p.product,
          totalPrice: p.price, // OR: calculate total if needed
          quantity: p.quantity || 1, // default 1 unless you track qty
          amount: p.price, // price renamed to amount
        });
      });
    });

    return products;
  };

  const handleConfirmOrder = async () => {
    if (!orderData || !id) return;

    try {
      const now = new Date();
      const orderId = `${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(
        2,
        "0"
      )}${String(now.getSeconds()).padStart(2, "0")}`;

      const orderPayload = {
        Products: formatProducts(orderData.serviceDetails), // 👈 NEW
        Customerid: orderData.customerId,
        totalAmount: calculateRemainingAmount() + receivedAmount, // 👈 totalOrderPrice → totalAmount
        RemainingAmount: calculateRemainingAmount(),
        CustomerName: orderData.Name || "",
        CustomerPhoneNo: orderData.phone_number || "",
        paymentHistory: [
          {
            Paymentmode: orderData.payment?.paymentMethod,
            ReceivedAmount: receivedAmount,
          },
        ],
        payments: [
          {
            Paymentmode: orderData.payment?.paymentMethod,
            ReceivedAmount: receivedAmount,
          },
        ],
        OrderStatus: "Confirm",
        Orderdate: `${time.year}-${time.month}-${time.day}`,
        Ordertime: `${time.hours}:${time.minutes}`,
        cars: groupServicesByCar(),
      };

      const response = await confirmOrder(orderPayload);

      if (response.Id) {
        const customerUpdatePayload = {
          customerDedails: {
            RemainingAmount: calculateRemainingAmount(),
            ReceivedAmount: receivedAmount,
            Orderid: orderId,
            CustomerName: orderData.CustomerName || orderData.Name || "",
            CustomerPhoneNo: orderData.CustomerPhoneNo || orderData.phone_number || "",
          },
        };

        await updateCustomer(customerUpdatePayload);

        // ✅ Return the confirmed data instead of navigation
        return {
          ...orderPayload,
          Id: response.Id,
        };
      }
    } catch (err) {
      console.error("Error processing order:", err);
    }
  };

  return {
    receivedAmount,
    setReceivedAmount,
    calculateRemainingAmount,
    handleConfirmOrder,
  };
};
